import React, { useEffect, useState, useRef } from 'react';
import { Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Registerer, SessionState, UserAgent } from 'sip.js';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';

// COMPONENT IMPORTS
import config from '../../config';
import Timer from '../../utils/Timer';
import { socket } from '../../config/Socket';
import DtmfScreen from './dialScreens/DtmfScreen';
import DialPadScreen from './dialScreens/DialPadScreen';
import TransferScreen from './dialScreens/TransferScreen';
import ringtoneMp3 from '../../assets/sound/ringtone.mp3';
import { useCallingData } from '../Context/CallingContext';
import DefaultLiveCall from './dialScreens/DefaultLiveCall';
import ConferenceScreen from './dialScreens/ConferenceScreen';
import UserTransferScreen from './dialScreens/UserTransferScreen';
import IncomingCallScreen from './dialScreens/IncomingCallScreen';
import AgentTransferScreen from './dialScreens/AgentTransferScreen';
import ConferenceLiveScreen from './dialScreens/ConferenceLiveScreen';
import TransferHangupScreen from './dialScreens/TransferHangupScreen';
import { formatPayloadDate, getCurrentDateTime } from '../../utils/common';
import { isOnBreakNow, setBreakPending } from '../../utils/breakState';
import { fetchCrmAndLand } from '../../utils/crmLanding';
import {
  clearActiveSession,
  getMediaLiveness,
  resetMediaLiveness,
  setActiveSession,
  setSoftphoneEnabled,
  subscribeSessionEnded
} from '../../utils/mediaLiveness';
import {
  DECISION,
  GUARD_STATE,
  HANGUP_HOLD_MAX_MS,
  HANGUP_HOLD_MS,
  STUCK_CALL_MS,
  decideEvent,
  guardStateForStatus,
  normalizeCallEvent
} from '../../utils/callEventGuard';
import { toast } from 'react-toastify';

// APIS IMPORTS
import { setCallingModalOpen, setSidebarType, useTimezone } from '../../store/slices/commonSlice';
import { authLogout, useUserDetails } from '../../store/slices/authSlice';
import { clearPersister } from '../../utils/common';
import {
  onSetCallStatus,
  onSetCallType,
  setBreakDetails,
  setCallDialSec,
  setCallEnd,
  setCallId,
  setCallNumber,
  setCurrentCallDetail,
  setIsDialNumber,
  setDialNumberType,
  setIsReDial,
  setScreenType,
  setSelectBreakType,
  useBreakDetails,
  useBreakType,
  useCallId,
  useCallingStatus,
  useCurrentCallDetail,
  useScreenType,
  useIsDialNumber,
  useDialNumberType,
  useDialSec,
  useIsReDial,
  useCallEnd,
  useHoldTime,
  setHoldTime,
  setHoldStartTime,
  useHoldStartTime,
  setSipStatus,
  useCallType,
  useSipStatus,
  useConferenceDetail,
  setConferenceDetail
} from '../../store/slices/dialSlice';
import {
  breaksListGet,
  crmInfoLog,
  manualCrmInfo,
  followUpAdd,
  handleConference,
  hangupCall,
  manualCall,
  manualDialing,
  pauseModeUpdate,
  progressiveCall,
  removeCrmDetails,
  selectBreak,
  setIdleCall,
  setPauseMode,
  setUnregisterCall,
  updateCrmInformation,
  useBreaksList,
  useCrmDetails,
  usePauseMode
} from '../../store/slices/callingSlice';
import MicrophonePermissionModal from '../Modals/MicrophonePermissionModal';
import { playBeep } from '../../utils/audioUtils';

const CallingSidebar = ({ setIsProcessModalOpen, isMini = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const isEmbedded = window.self !== window.top;

  const {
    informationData,
    dispositionsData,
    fieldsData,
    followUp,
    isPauseMode,
    setFollowUp,
    setIsPauseMode,
    handleTimeReset,
    setMutedConference,
    setOnHoldConference,
    showMicModal,
    micPermission,
    requestMicrophonePermission
  } = useCallingData();
  const pauseMode = usePauseMode();
  const user = useUserDetails();
  const callStatus = useCallingStatus();
  const callId = useCallId();
  const breaksList = useBreaksList();
  const breakType = useBreakType();
  const screenType = useScreenType();
  const isDialNumber = useIsDialNumber();
  const dialNumberType = useDialNumberType();
  const breakDetails = useBreakDetails();
  const CurrentCallDetail = useCurrentCallDetail();
  const crmDetails = useCrmDetails();
  const dialSec = useDialSec();
  const isReDial = useIsReDial();
  const callEnd = useCallEnd();
  const holdTime = useHoldTime();
  const holdStartTime = useHoldStartTime();
  const callType = useCallType();
  const sipStatus = useSipStatus();
  const conferenceDetail = useConferenceDetail();

  const [isOpen, setIsOpen] = useState(false);
  const [isConVoxOpen, setIsConVoxOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [duration, setDuration] = useState(0);
  const [wrapsec, setWrapsec] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isBreakLoading, setIsBreakLoading] = useState(false);

  const intervalRef = useRef(null);
  const incomingInvitationRef = useRef(null);
  const reconnectInProgressRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const ringtoneRef = useRef(null);
  const updateCrmRef = useRef();
  // Refs so socket handlers always read the latest value without stale closures
  const breakDetailsRef = useRef(breakDetails);
  const sipStatusRef = useRef(sipStatus);
  const callStatusRef = useRef(callStatus);
  // The live call's uuid, readable synchronously from socket handlers (callId is not
  // in the sendMessage effect's deps, so its closure can be stale).
  const callIdRef = useRef(callId);
  // Capped history of every uuid that has been a live call. Used to POSITIVELY
  // identify a stale HANGUP: one whose uuid we already know belonged to an earlier
  // call. A HANGUP whose uuid we have never seen live is passed through untouched —
  // manual/progressive calls seed callId from the originate response, and the
  // backend's HANGUP uuid is not guaranteed to match it, so we must never drop an
  // unrecognised teardown or the call is stranded "ON CALL" forever.
  const seenCallUuidsRef = useRef([]);
  // Every uuid belonging to the call IN PROGRESS — a set, not a single value, because a
  // call does not have one identifier. Manual/progressive/preview calls seed callId from
  // the originate response and the switch's later events for that same call may carry a
  // different uuid (see the HANGUP guard below). Comparing against one value would drop
  // real teardowns; comparing against the set is what makes a strict check safe.
  const currentCallUuidsRef = useRef(new Set());
  // The event-order guard's own view of the call. A ref, not module state: this
  // component is mounted twice (MainLayout renders a mini instance) and module state
  // would be shared between them and survive logout.
  const callGuardRef = useRef({ state: GUARD_STATE.IDLE });
  // A teardown we could not attribute to the live call, withheld while the media is
  // still up. `{ message, timerId, maxTimerId }`.
  const pendingHangupRef = useRef(null);
  // Always the freshest socket handler, so timers and media-ended callbacks can release
  // a held teardown through the same path a live event takes.
  const handleMessageRef = useRef(null);
  // Which callId the agent has successfully landed on the CRM screen for. Latched per
  // call: once landed, the watchdog stops for that call, so a deliberate user
  // navigation away from /crm is never fought.
  const landedCallIdRef = useRef(null);
  // Serializes CRM fetch attempts so the watchdog never stacks a second fetch on top
  // of one still in flight (retries alone can span several seconds).
  const crmFetchInFlightRef = useRef(false);

  // Record a uuid as one we have seen live, so a later HANGUP for it can be
  // recognised as stale. Capped so a full shift of calls can't grow it unbounded.
  const markCallUuidSeen = (uuid) => {
    if (!uuid) return;
    const seen = seenCallUuidsRef.current;
    if (seen.includes(uuid)) return;
    seen.push(uuid);
    if (seen.length > 200) seen.splice(0, seen.length - 200);
  };

  // Claim a uuid for the call in progress. Every dial path funnels here through the
  // `callId` sync effect below, including DialPadScreen and LeadPreview, which dispatch
  // `setCallId` from outside this component and cannot reach these refs directly.
  const markCallUuidCurrent = (uuid) => {
    if (!uuid) return;
    markCallUuidSeen(uuid);
    currentCallUuidsRef.current.add(uuid);
  };

  const clearPendingHangup = () => {
    const pending = pendingHangupRef.current;
    if (!pending) return;
    if (pending.timerId) clearTimeout(pending.timerId);
    if (pending.maxTimerId) clearTimeout(pending.maxTimerId);
    pendingHangupRef.current = null;
  };

  // Back to "no call". Called whenever the call ends by ANY route — see the
  // reconciliation effect below for why that is a single condition rather than a list.
  const resetCallGuard = () => {
    clearPendingHangup();
    currentCallUuidsRef.current = new Set();
    callGuardRef.current = { state: GUARD_STATE.IDLE };
  };

  // Guard decisions go to the same backend log pipeline as the raw socket messages, so
  // a complaint can be traced to the exact event that caused it. Only non-accepted
  // decisions are sent — accepting is the common case and is already covered by the
  // 'socket msg' log above.
  const logGuardEvent = (decision, detail) => {
    const payload = { tag: 'call-event-guard', decision, ...detail };
    console.log('[call-event-guard]', payload);
    getLogDetails({
      user_id: user?.user_id,
      message: 'call event guard',
      'socket.id': socket.id,
      message_data: payload
    });
  };

  // Apply a teardown we had withheld, through the same path a live event takes.
  const releasePendingHangup = (reason) => {
    const pending = pendingHangupRef.current;
    if (!pending) return;
    clearPendingHangup();
    logGuardEvent('held-released', { reason, uuid: pending.message?.origination_uuid });
    handleMessageRef.current?.({ message: pending.message, forceAccept: true });
  };

  // Withhold an unattributable teardown rather than applying it over a live call or
  // discarding it outright. Released by whichever comes first: the media session
  // actually ending, the short hold expiring with no media signal to wait on, or the
  // hard cap — which exists so that a mis-tracked session can never strand the agent
  // ON CALL forever. Holding while the media is genuinely up is not a stall: the call
  // really is still connected, so refusing to wrap it is the correct answer.
  const holdHangup = (message) => {
    clearPendingHangup();
    const timerId = setTimeout(() => {
      if (getMediaLiveness() === 'live') return;
      releasePendingHangup('hold-expired');
    }, HANGUP_HOLD_MS);
    const maxTimerId = setTimeout(() => releasePendingHangup('hold-cap'), HANGUP_HOLD_MAX_MS);
    pendingHangupRef.current = { message, timerId, maxTimerId };
  };

  // Keep refs in sync with the latest Redux state on every render
  useEffect(() => {
    breakDetailsRef.current = breakDetails;
  }, [breakDetails]);
  useEffect(() => {
    sipStatusRef.current = sipStatus;
  }, [sipStatus]);
  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);
  useEffect(() => {
    callIdRef.current = callId;
    markCallUuidCurrent(callId);
  }, [callId]);

  // THE anti-latch mechanism. Wrapup has at least eight exits — CRM submit from the
  // sidebar, CRM submit from /crm, set-idle, the force-idle fallback, the break
  // backstop, the embedded call_hangup reset, redial, and logout — and a guard that
  // only reset on some of them would eventually latch and leave the agent deaf to every
  // call event. Rather than enumerate the exits and hope the list stays complete, note
  // that all of them funnel through `callStatus` becoming null, and reconcile on that
  // one condition. Deliberately one-directional: Redux going idle resets the guard, but
  // the guard is never overridden while a call is in flight, or it could not do its job.
  useEffect(() => {
    if (isMini) return;
    if (callStatus) return;
    if (callGuardRef.current.state === GUARD_STATE.IDLE && !pendingHangupRef.current) return;
    resetCallGuard();
  }, [callStatus, isMini]);

  // `web_phone === 0` is the flag that ENABLES the browser softphone — see the SIP
  // registration effect below, which connects only in that case. Any other value means
  // the agent is on an external phone, there is no browser session to read, and media
  // liveness must stay unknowable so it never gates anything for them.
  useEffect(() => {
    setSoftphoneEnabled(Number(user?.web_phone) === 0);
  }, [user?.web_phone]);

  // The moment the media really ends, apply any teardown we were holding back. This is
  // the primary release path — the timers behind it are only there for the cases where
  // no media signal exists to wait on.
  useEffect(() => {
    if (isMini) return;
    return subscribeSessionEnded(() => releasePendingHangup('media-ended'));
  }, [isMini]);

  // A reload does not end an external-phone agent's call, but it does reset every ref
  // in here — which would leave the guard IDLE while a call is genuinely in progress,
  // and an IDLE guard has nothing to protect and lets any teardown through. Seed it
  // from the status that was persisted with the rest of the `dial` slice.
  useEffect(() => {
    if (isMini) return;
    if (!callStatusRef.current) return;
    callGuardRef.current = { state: guardStateForStatus(callStatusRef.current) };
  }, []);

  // Stuck-call watchdog — the system-level escape hatch.
  //
  // Everything else here is built to stop a call being wrapped up too EARLY. This is
  // the counterweight: if the media has been gone for a while and no teardown ever
  // arrived, the agent is parked on a call that no longer exists. That can happen
  // because a HANGUP was genuinely lost (which predates all of this), because a reload
  // landed inside a hold window, or because the hold logic itself got it wrong.
  //
  // Only for softphone agents: liveness is the whole basis of the decision, and it is
  // permanently 'unknown' for anyone on an external phone. Only from IN_CALL, so a call
  // still ringing — which has no media yet by definition — is never touched.
  useEffect(() => {
    if (isMini || isEmbedded) return;
    if (callStatus !== 'ON CALL') return;

    // Only ever act on a call whose media we positively saw come UP and then go away.
    // Without this the watchdog would fire on a call whose media simply never
    // established — predictive maps EXTENSION_RINGING straight to ON CALL, so there is
    // a real window where the status is live and the INVITE has not landed yet — and it
    // would recreate the very bug this change exists to fix. If the media comes and goes
    // inside one tick we simply never arm, which is the safe direction to fail.
    let sawLive = false;
    let endedSince = null;
    const tick = () => {
      if (isOnBreakNow() || callGuardRef.current.state !== GUARD_STATE.IN_CALL) {
        endedSince = null;
        return;
      }
      const liveness = getMediaLiveness();
      if (liveness === 'live') {
        sawLive = true;
        endedSince = null;
        return;
      }
      if (!sawLive || liveness !== 'ended') {
        endedSince = null;
        return;
      }
      if (endedSince === null) {
        endedSince = Date.now();
        return;
      }
      if (Date.now() - endedSince < STUCK_CALL_MS) return;

      logGuardEvent('stuck-call-recovered', {
        callId: callIdRef.current,
        heldTeardown: !!pendingHangupRef.current
      });
      clearPendingHangup();
      callGuardRef.current = { state: GUARD_STATE.WRAPUP };
      dispatch(onSetCallStatus('WRAPUP'));
    };

    const watchdog = setInterval(tick, 5000);
    return () => clearInterval(watchdog);
  }, [callStatus, isMini, isEmbedded]);

  const onLogout = async () => {
    try {
      await dispatch(authLogout()).unwrap();
      socket.emit('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
      socket.disconnect();
      socket.off('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
      clearPersister(dispatch);
      // Module-level, so unlike component state it would otherwise survive into the
      // next agent's session on this tab.
      resetMediaLiveness();
      resetCallGuard();
      window.__SIP_MANUAL_LOGOUT__ = true;
      localStorage.clear();
      localStorage.removeItem('timerDuration');
      localStorage.removeItem('timerStartTime');
      navigate('/login');
    } catch (error) {
      console.log('error', error);
    }
  };

  const userAgentRef = useRef(null);
  const registererRef = useRef(null);
  const registerRetryTimeoutRef = useRef(null);
  const registerRetryAttemptsRef = useRef(0);

  window.__SIP_MANUAL_LOGOUT__ = false;

  const clearRegisterRetry = () => {
    if (registerRetryTimeoutRef.current) {
      clearTimeout(registerRetryTimeoutRef.current);
      registerRetryTimeoutRef.current = null;
    }
  };

  // SIP.js never retries a rejected REGISTER (only 423). A transient reject —
  // e.g. FreeSWITCH answering 503 while it restarts — otherwise leaves the phone
  // un-registered forever, because the transport stays connected and none of the
  // transport-level reconnect paths ever fire again.
  const scheduleRegisterRetry = (retryAfterSeconds) => {
    if (window.__SIP_MANUAL_LOGOUT__ || !userAgentRef.current) return;
    clearRegisterRetry();
    const attempt = registerRetryAttemptsRef.current;
    registerRetryAttemptsRef.current = attempt + 1;
    // Honour the server's Retry-After when it sends one, otherwise back off.
    const delay = Number(retryAfterSeconds)
      ? Number(retryAfterSeconds) * 1000
      : Math.min(30000, 2000 * 2 ** attempt);
    console.log(`REGISTER retry scheduled in ${delay / 1000}s`);
    registerRetryTimeoutRef.current = setTimeout(() => {
      registerRetryTimeoutRef.current = null;
      if (window.__SIP_MANUAL_LOGOUT__ || !userAgentRef.current) return;
      registerUA();
    }, delay);
  };

  // `stateChange` only fires on an actual transition, so a REGISTER that succeeds
  // while the registerer is already Registered emits nothing — which used to leave
  // the header stuck on "Connecting". These delegates fire on every response.
  const registerRequestDelegate = {
    onAccept: () => {
      console.log('✅ SIP Registered Successfully');
      registerRetryAttemptsRef.current = 0;
      clearRegisterRetry();
      dispatch(setSipStatus('Register'));
    },
    onReject: (response) => {
      const statusCode = response?.message?.statusCode;
      console.warn(`❌ REGISTER rejected with ${statusCode}`);
      dispatch(setSipStatus('Un-Register'));
      scheduleRegisterRetry(registererRef.current?.retryAfter);
    }
  };

  // const remoteStream = new MediaStream();

  // Publish the live media session so the wrapup guards can ask whether the agent is
  // still actually talking. Every call type — predictive, progressive, manual, preview,
  // incoming — reaches the agent as an inbound INVITE, so attaching here covers all of
  // them, both the auto-answer branch and the one the agent accepts by hand.
  //
  // This is why the session is tracked separately from `incomingInvitationRef`: that ref
  // is cleared the moment the call is answered, which is precisely when we start needing
  // to know the session is up.
  const trackSipSession = (session) => {
    if (!session?.stateChange) return;
    session.stateChange.addListener((state) => {
      if (state === SessionState.Established) setActiveSession(session);
      if (state === SessionState.Terminated) clearActiveSession(session);
    });
  };

  const connectSIP = async () => {
    try {
      window.__SIP_MANUAL_LOGOUT__ = false;
      const sipUri = UserAgent.makeURI(
        `sip:${user.username}@${user.domain}:${process.env.REACT_APP_SIP_PORT}`
      );
      if (!sipUri) {
        console.error('Invalid SIP URI');
        return;
      }

      const userAgentOptions = {
        uri: sipUri,
        authorizationPassword: user?.password,
        authorizationUsername: user?.username,
        transportOptions: {
          server: config.WSS_URL,
          keepAliveInterval: 20,
          keepAliveDebounce: 10,
          connectionTimeout: 10
        }
        // sessionDescriptionHandlerFactoryOptions: {
        //   peerConnectionConfiguration: {
        //     iceServers: [
        //       { urls: 'stun:stun.l.google.com:19302' },
        //       { urls: 'stun:stun1.l.google.com:19302' }
        //     ]
        //   }
        // }
      };
      dispatch(setSipStatus('Connecting'));
      console.log('Creating UserAgent...');
      userAgentRef.current = new UserAgent(userAgentOptions);

      console.log('Starting UserAgent...');
      await userAgentRef.current.start();
      userAgentRef.current.transport.stateChange.addListener((state) => {
        if (state === 'Disconnected' && !window.__SIP_MANUAL_LOGOUT__) {
          reconnectUA();
        }
      });

      console.log('Registering UserAgent...');
      registererRef.current = new Registerer(userAgentRef.current);

      registererRef.current.stateChange.addListener((state) => {
        switch (state) {
          case 'Registered':
            console.log('✅ SIP Registered Successfully');
            dispatch(setSipStatus('Register'));
            break;

          case 'Unregistered':
            console.log('❌ SIP Unregistered');
            dispatch(setSipStatus('Un-Register'));
            break;

          case 'Terminated':
            console.log('⚠️ SIP Registration Terminated');
            dispatch(setSipStatus('Un-Register'));
            break;
        }
      });

      await registererRef.current.register({ requestDelegate: registerRequestDelegate });

      console.log('UserAgent registered and ready for calls.');
      userAgentRef.current.delegate = {
        onInvite: async (invitation) => {
          console.log('Incoming call...');
          // `userAgent.stop()` on break is not awaited, so an INVITE can still arrive
          // here after the agent has gone on break — and the else-branch below would
          // auto-answer it. Reject it instead of ringing or accepting.
          if (isOnBreakNow()) {
            console.log('Rejecting incoming call — agent is on break');
            try {
              await invitation.reject();
            } catch (err) {
              console.error('Error rejecting call while on break:', err);
            }
            return;
          }
          incomingInvitationRef.current = invitation;
          trackSipSession(invitation);
          const userProcessType = user?.process_details?.type;
          if (
            process.env.REACT_APP_INCOMING_SCREEN === 'true' &&
            (userProcessType === 'InboundAndManual' ||
              userProcessType === 'BlendedAndPredictive' ||
              userProcessType === 'BlendedAndPreview' ||
              userProcessType === 'BlendedAndProgressive')
          ) {
            playRingtone();

            invitation.stateChange.addListener((state) => {
              if (state === SessionState.Terminated) {
                stopRingtone();
                incomingInvitationRef.current = null;
              }
            });
          } else {
            try {
              await invitation.accept({
                sessionDescriptionHandlerOptions: {
                  constraints: {
                    audio: true,
                    video: false
                  }
                }
              });
              setupRemoteMedia(invitation);
              console.log('Call answered!');
            } catch (err) {
              console.error('Error answering call:', err);
            }
          }
        }
      };
    } catch (error) {
      console.error('SIP Connection Error:', error);
    }
  };

  const answerCall = async () => {
    const invitation = incomingInvitationRef.current;
    if (!invitation) return;
    stopRingtone();

    try {
      await invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false
          }
        }
      });
      setupRemoteMedia(invitation);
      console.log('Incoming call answered!');
      incomingInvitationRef.current = null;
    } catch (err) {
      incomingInvitationRef.current = null;
      console.error('Error answering Incoming call:', err);
    }
  };

  const registerUA = async () => {
    if (!userAgentRef.current) return;

    try {
      if (!registererRef.current || registererRef.current.state === 'Terminated') {
        registererRef.current = new Registerer(userAgentRef.current);
        registererRef.current.stateChange.addListener((state) => {
          switch (state) {
            case 'Registered':
              console.log('✅ SIP Registered Successfully');
              dispatch(setSipStatus('Register'));
              break;
            case 'Unregistered':
              console.log('❌ SIP Unregistered');
              dispatch(setSipStatus('Un-Register'));
              break;
            case 'Terminated':
              console.log('⚠️ SIP Registration Terminated');
              dispatch(setSipStatus('Un-Register'));
              break;
          }
        });
      }

      // NOTE: register() resolves as soon as the request is SENT - a 4xx/5xx reply
      // never rejects this promise. The outcome arrives via registerRequestDelegate.
      registererRef.current
        .register({ requestDelegate: registerRequestDelegate })
        .then(async () => {
          console.log('UA Register request sent ...');
        })
        .catch(async (error) => {
          console.error('Failed to send REGISTER ==> ', error);
          scheduleRegisterRetry();
        });
    } catch (error) {
      console.error('CATCH FOUND -  registerUA ==> ', error);
    }
  };

  const reconnectUA = async () => {
    if (reconnectInProgressRef.current || window.__SIP_MANUAL_LOGOUT__ || !userAgentRef.current) {
      return false;
    }
    reconnectInProgressRef.current = true;
    try {
      console.log('Connecting------------->');
      await userAgentRef.current.reconnect();
      dispatch(setSipStatus('Connecting'));
      await registerUA();
      // Connected again — clear any pending retry and reset the backoff.
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    } catch (error) {
      console.error('UA Reconnection error ==> ', error);
      // A single attempt isn't enough: when the machine is idle/asleep the network
      // can stay suspended far longer than one reconnect call. Keep retrying with a
      // capped exponential backoff so the phone re-registers once the network is back.
      if (!window.__SIP_MANUAL_LOGOUT__ && userAgentRef.current) {
        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(30000, 2000 * 2 ** attempt);
        reconnectAttemptsRef.current = attempt + 1;
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          reconnectUA();
        }, delay);
      }
    } finally {
      reconnectInProgressRef.current = false;
    }
  };

  // const setupRemoteMedia = (mediaSession) => {
  //   const mediaElement = document.getElementById('mediaElement');
  //   try {
  //     mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
  //       if (receiver.track) {
  //         remoteStream.addTrack(receiver.track);
  //         mediaElement.srcObject = remoteStream;
  //         mediaElement.play();
  //       }
  //     });
  //   } catch (error) {
  //     console.log('Media audio session error - ', error);
  //   }
  // };

  const setupRemoteMedia = (mediaSession) => {
    const mediaElement = document.getElementById('mediaElement');
    if (!mediaElement) return;

    try {
      mediaElement.pause();
      mediaElement.srcObject = null;

      const stream = new MediaStream();

      mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
        if (receiver.track && receiver.track.kind === 'audio') {
          stream.addTrack(receiver.track);
        }
      });

      mediaElement.srcObject = stream;

      mediaElement.play().catch(() => {
        console.warn('Audio play interrupted or blocked by browser');
      });
    } catch (error) {
      console.log('Media audio session error - ', error);
    }
  };

  const playRingtone = () => {
    if (!ringtoneRef.current) return;
    if (!ringtoneRef.current.paused) return;

    ringtoneRef.current.play().catch(() => console.warn('Ringtone blocked'));
  };

  const stopRingtone = () => {
    if (!ringtoneRef.current) return;
    ringtoneRef.current.pause();
    ringtoneRef.current.currentTime = 0;
  };
  const isRingtonePlaying = () => !!ringtoneRef.current && !ringtoneRef.current.paused;

  useEffect(() => {
    if (isMini) return;
    ringtoneRef.current = new Audio(ringtoneMp3);
    ringtoneRef.current.loop = true;

    return () => {
      ringtoneRef.current?.pause();
      ringtoneRef.current = null;
    };
  }, [isMini]);

  useEffect(() => {
    if (isMini) return;
    const handleOnline = async () => {
      if (Number(user.web_phone) === 0) {
        reconnectAttemptsRef.current = 0;
        reconnectUA();
      }
    };

    const handleOffline = () => {
      if (Number(user.web_phone) === 0) {
        dispatch(setSipStatus('Un-Register'));
        // Was a blocking alert() - on a flaky link it froze the tab on every dip
        // and the agent had to dismiss it before doing anything else.
        toast.error('Please check your connection');
      }
    };

    // When the tab/machine resumes from sleep, Chrome often does NOT fire `online`,
    // so we also re-check on visibility. Only reconnect when the transport is not
    // already connected — never disrupt a healthy registration.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Number(user.web_phone) !== 0 || breakDetails) return;
      const transportState = userAgentRef.current?.transport?.state;
      if (transportState && transportState !== 'Connected') {
        reconnectAttemptsRef.current = 0;
        reconnectUA();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user.web_phone, isMini, breakDetails]);

  useEffect(() => {
    if (isMini) return;
    if (Number(user.web_phone) === 0 && !breakDetails) {
      reconnectInProgressRef.current = false;
      reconnectAttemptsRef.current = 0;
      Object.defineProperty(window, 'isSecureContext', { get: () => true });

      if (!userAgentRef.current) {
        connectSIP();
      } else {
        reconnectUA();
      }
    } else {
      reconnectInProgressRef.current = true;
      // Cancel any pending reconnect retry while SIP is intentionally disabled (break / web_phone off).
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      clearRegisterRetry();
      dispatch(setSipStatus('Un-Register'));
      console.log('SIP registration is disabled due to web_phone flag.');
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      clearRegisterRetry();
      if (userAgentRef.current) {
        dispatch(setSipStatus('Un-Register'));
        console.log('Stopping UserAgent...');
        userAgentRef.current.stop();
        userAgentRef.current = null;
      }
    };
  }, [user.web_phone, breakDetails, isMini]);

  useEffect(() => {
    if (isMini) return;
    let timerInterval;
    // No wrapup clock while on break — there is no call to wrap up.
    if (callStatus === 'WRAPUP' && !breakDetails) {
      timerInterval = setInterval(() => {
        setWrapsec((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => {
      clearInterval(timerInterval);
    };
  }, [callStatus, breakDetails, isMini]);

  useEffect(() => {
    if (isMini) return;
    // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    // const isAuto = autoWrapUpZero && wrapsec > autoWrapUp;
    const isAuto = wrapsec > autoWrapUp;
    // Independent safety net, and the one that actually protects the customer record.
    // A media session still Established while we are in wrapup is the signature of a
    // false WRAPUP — auto-submitting there writes an AUTOWRAP disposition against a call
    // the agent is still talking on. Scoped to AUTO submissions only: `callEnd` means
    // the agent ended the call themselves, and their own submit is never blocked.
    // 'unknown' (external phone, no browser session) must not block, or those agents
    // would never auto-wrap at all.
    if (isAuto && getMediaLiveness() === 'live') {
      // This effect re-runs every second while the wrapup clock ticks, so log only the
      // first tick past the threshold rather than once per second for the whole call.
      if (wrapsec === autoWrapUp + 1) {
        logGuardEvent('auto-submit-blocked', { source: 'sidebar', wrapsec });
      }
      return;
    }
    // In an iframe the host app owns the CRM update — never run updateCrm here.
    // Never auto-submit a CRM for an agent on break either: it would disposition a
    // call they are no longer on and push them back through the post-call flow.
    if (
      (isAuto || callEnd) &&
      !isLoading &&
      !isOnBreakNow() &&
      location.pathname !== '/crm' &&
      dialSec !== null &&
      !isEmbedded
    ) {
      updateCrmRef.current(isAuto);
    }
  }, [wrapsec, location.pathname, callEnd, isMini]);

  useEffect(() => {
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
    const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
    const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

    if (callStatus === 'WRAPUP' && autoWrapUp > 10 && location.pathname !== '/crm') {
      if (autoWrapUp - wrapsec === 5) {
        const needsDispo = dispoRequired && !dispositionsData?.dispositioncode;
        const needsSubDispo = subDispoRequired && !dispositionsData?.subdispositioncode;
        const needsSubSubDispo = subSubDispoRequired && !dispositionsData?.subsubdispositioncode;

        if (needsDispo || needsSubDispo || needsSubSubDispo) {
          toast.warning('Please fill the required disposition fields. 5 seconds remaining.');
        }
      }
    }
  }, [wrapsec, callStatus, location.pathname]);

  const onPauseModeChange = async (pause_mode) => {
    try {
      const payload = { user_id: user?.id, pause_mode: pause_mode };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleConferenceMode = async () => {
    try {
      const payload = {
        conf_id: 'all',
        type: 'hangup',
        conf_name: conferenceDetail?.conference_name
      };
      await dispatch(handleConference(payload)).unwrap();
      dispatch(setConferenceDetail(null));
      setMutedConference([]);
      setOnHoldConference([]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const updateCrm = async (isAuto = false) => {
    setIsLoading(true);
    const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
    const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
    const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

    if (!isAuto && !isReDial) {
      if (dispoRequired && !dispositionsData?.dispositioncode) {
        toast.error('Please select a disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
      if (subDispoRequired && !dispositionsData?.subdispositioncode) {
        toast.error('Please select a sub disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
      if (subSubDispoRequired && !dispositionsData?.subsubdispositioncode) {
        toast.error('Please select a sub-sub disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
    }

    if (conferenceDetail && conferenceDetail?.details?.length > 0) {
      handleConferenceMode();
    }
    try {
      // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
      const autoWrapup = Number(user?.process_details?.autoWrapup) || 10;

      let hold_time = 0;

      if (holdStartTime !== null) {
        const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
        hold_time = holdTime + holdDuration;
      } else {
        hold_time = holdTime;
      }

      const payload = {
        data: [
          {
            crmDetails: { ...informationData }
          },
          {
            fields: fieldsData?.fields ? fieldsData?.fields : []
          },
          {
            dispositions: dispositionsData?.dispositioncode
              ? { ...dispositionsData }
              : { dispositioncode: 'AUTOWRAP' }
          },
          {
            unique_id: crmDetails?.[4]?.unique_id,
            dialsec: callType === 'predictive' || callType === 'incoming' ? 0 : dialSec,
            hold_time: hold_time,
            wrapsec: wrapsec,
            // auto_wrapup: !autoWrapUpZero ? autoWrapup <= wrapsec : false
            auto_wrapup: autoWrapup <= wrapsec
          }
        ]
      };
      if (followUp?.isFollowUp === 'yes') {
        dispatch(
          followUpAdd({
            phone_number: crmDetails?.[1]?.crmDetails?.primaryNumber,
            crm_id: crmDetails?.[1]?.crmDetails?.Crms_id,
            date_and_time: formatPayloadDate(followUp?.date_and_time)
          })
        ).unwrap();
      }
      if (isPauseMode) {
        onPauseModeChange(1);
      }
      const data_message = {
        user_id: user.user_id,
        payload: payload,
        message: 'update Crm Information'
      };
      getLogDetails(data_message);
      const response = await dispatch(updateCrmInformation(payload)).unwrap();
      if (response) {
        const REDIAL_DELAY_MS = 4000;
        const NORMAL_DELAY_MS = 500;
        const postUpdateDelay = isReDial ? REDIAL_DELAY_MS : NORMAL_DELAY_MS;

        const responseTimer = setTimeout(() => {
          if (isReDial) {
            dispatch(setIsDialNumber(crmDetails?.[1]?.crmDetails?.primaryNumber));
          }
          setWrapsec(0);
          dispatch(setCallId(null));
          dispatch(setCallingModalOpen(user?.manualcall ? true : false));
          dispatch(setCallDialSec(null));
          dispatch(setCurrentCallDetail(null));
          dispatch(setSidebarType('main'));
          dispatch(setCallEnd(false));
          handleTimeReset();
          dispatch(onSetCallStatus(null));
          dispatch(onSetCallType(null));
          if (location.pathname !== '/chat') {
            navigate('/dashboard');
          }
          setIsLoading(false);
          dispatch(setHoldTime(0));
          dispatch(setHoldStartTime(null));
          setFollowUp({
            date_and_time: startOfDay(today),
            isFollowUp: 'no'
          });
          setIsPauseMode(false);
        }, postUpdateDelay);

        // const removeTimer = setTimeout(() => {
        //   dispatch(removeCrmDetails());
        // }, 600);

        return () => {
          clearTimeout(responseTimer);
          // clearTimeout(removeTimer);
        };
      }
    } catch (error) {
      setIsLoading(false);
      const data_message = {
        user_id: user.user_id,
        payload: error?.message,
        message: 'update Crm Information error'
      };
      getLogDetails(data_message);
      console.log('error', error);
    }
  };

  updateCrmRef.current = updateCrm;

  // Tear down call state without submitting a CRM and without navigating. Used
  // whenever a call ends while the agent is on break: the state must not be left
  // stranded, but nothing call-related may surface over the break UI either.
  const resetCallState = () => {
    setWrapsec(0);
    dispatch(setCallId(null));
    dispatch(setCurrentCallDetail(null));
    dispatch(setCallDialSec(null));
    dispatch(setCallEnd(false));
    dispatch(onSetCallStatus(null));
    dispatch(onSetCallType(null));
    dispatch(setScreenType(''));
    dispatch(setHoldTime(0));
    dispatch(setHoldStartTime(null));
    dispatch(setSidebarType('main'));
    incomingInvitationRef.current = null;
    handleTimeReset();
  };

  // When embedded in an iframe, ending a call must ONLY hang up the call — the host
  // app owns the CRM disposition/update, so we never run updateCrm here. We also do
  // NOT reset local state at this point: the backend emits a `call_hangup` socket
  // event once the call is actually torn down, and that is what resets everything.
  const onIframeHangup = async () => {
    try {
      if (callId) {
        await dispatch(hangupCall({ call_uuid: callId })).unwrap();
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isMini) return;
    if (!isEmbedded) return;
    if (!callEnd) return;
    onIframeHangup();
  }, [callEnd, isMini]);

  // The backend emits `call_hangup` once the call is fully torn down (whether the
  // agent or the remote party ended it). That is our single cue to reset all
  // calling state and return to the dashboard.
  useEffect(() => {
    if (!isEmbedded) return;

    const handleCallHangup = () => {
      console.log('call_hangup socket');
      setWrapsec(0);
      dispatch(setCallId(null));
      dispatch(setCurrentCallDetail(null));
      dispatch(setCallingModalOpen(user?.manualcall ? true : false));
      dispatch(setCallDialSec(null));
      dispatch(setCallEnd(false));
      dispatch(onSetCallStatus(null));
      dispatch(onSetCallType(null));
      dispatch(setHoldTime(0));
      dispatch(setHoldStartTime(null));
      dispatch(setScreenType(''));
      handleTimeReset();
      incomingInvitationRef.current = null;
      dispatch(setSidebarType('main'));
      // This is the only teardown signal in embedded mode, so all the state resets
      // above must always run — suppressing them would strand callId/callStatus
      // forever. Only the navigation is suppressed on break: it would pull the agent
      // out from under the break UI and back to the dashboard mid-break.
      if (isOnBreakNow()) return;
      navigate('/dashboard');
    };

    socket.on('call_hangup', handleCallHangup);
    return () => {
      socket.off('call_hangup', handleCallHangup);
    };
  }, [isEmbedded, user]);

  useEffect(() => {
    if (callStatus === 'WRAPUP' || !callStatus) {
      dispatch(setScreenType(''));
      setNumber('');
      dispatch(setCallNumber(''));
      setWrapsec(0);
    }
  }, [callStatus, breakDetails]);

  // Backstop: while a break is active, no call UI may remain on screen. The guards
  // above stop call state from being *created* during a break; this clears anything
  // that slipped in just before the break committed, and — because the whole `dial`
  // slice is persisted — anything rehydrated by a reload taken during a break.
  useEffect(() => {
    if (isMini) return;
    if (!breakDetails) return;
    // Route first, unconditionally: call state may already have been cleared by
    // another path, which would leave the agent parked on a dead CRM route for the
    // whole break and still there when it ends.
    if (location.pathname === '/crm' || location.pathname === '/call-in-progress') {
      navigate('/dashboard');
    }
    if (!callStatus && !screenType) return;
    resetCallState();
  }, [breakDetails, callStatus, screenType, isMini, location.pathname]);

  useEffect(() => {
    if (isMini) return;
    socket.emit('identify', { user_id: user.user_id, type: user?.type });

    const handleServerRestart = ({ message }) => {
      console.log('server_restart-------->', message);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    // `forceAccept` is set only when a previously held teardown is being released, so
    // the hold policy does not run again and re-hold the same event.
    const handleMessage = ({ message, forceAccept = false }) => {
      console.log('socket msg-->', message);
      console.log('socket id-->', socket.id);
      const data_message = {
        user_id: user.user_id,
        message: 'socket msg',
        'socket.id': socket.id,
        message_data: message
      };
      getLogDetails(data_message);
      // On break we accept no call-status messages at all. `isOnBreakNow()` latches
      // from the moment the agent clicks a break rather than when the break API
      // resolves, so this also covers the window where the switch has already
      // dropped the agent but `breakDetails` has not landed yet — the window that
      // used to let a HANGUP through as WRAPUP + CRM.
      if (
        isOnBreakNow() ||
        (sipStatusRef.current === 'Un-Register' && Number(user.web_phone) === 0)
      ) {
        // Nothing is torn down here on purpose. A call left live by the break is
        // cleaned up by the backstop effect once `breakDetails` commits, which keeps
        // that teardown out of the unrelated `Un-Register` case above — there, a
        // transient de-registration during a network flap would otherwise destroy a
        // still-live call's state.
        return false;
      }
      if (
        (!!breakDetailsRef.current || callStatusRef.current) &&
        message?.event_type === 'EXTENSION_RINGING'
      ) {
        return false;
      }
      // Late, duplicate, or out-of-order HANGUPs from a PREVIOUS call used to flip
      // the live call straight to WRAPUP (the status mapping below never matched
      // uuids), which auto-submitted the CRM and bounced the agent to /dashboard
      // mid-call with no way back until the next call.
      //
      // The original rule here could only drop a HANGUP it could POSITIVELY identify as
      // stale — different uuid AND already seen. That left three ways through while a
      // call was live: a uuid we had never seen (B-leg, bridge, agent-leg), a HANGUP
      // with no uuid at all, and any unmapped event_type, which produced a null status
      // and wiped ON CALL. utils/callEventGuard.js closes all three; its policy still
      // contains the original rule, so nothing that used to be caught is let through.
      const event = normalizeCallEvent(message);
      const previousState = callGuardRef.current.state;
      const verdict = decideEvent({
        event,
        guardState: previousState,
        currentUuids: currentCallUuidsRef.current,
        seenUuids: seenCallUuidsRef.current,
        mediaLiveness: getMediaLiveness(),
        forceAccept
      });

      if (verdict.decision !== DECISION.ACCEPT) {
        logGuardEvent(verdict.decision, {
          reason: verdict.reason,
          eventType: event.eventType,
          callType: event.callType,
          uuid: event.uuid,
          guardState: previousState,
          callId: callIdRef.current,
          mediaLiveness: getMediaLiveness()
        });
      }

      if (verdict.decision === DECISION.DROP) return false;
      if (verdict.decision === DECISION.HOLD) {
        holdHangup(message);
        return false;
      }
      // An accepted teardown supersedes anything still being held for this call.
      if (event.isHangupSignal) clearPendingHangup();
      // PASS keeps the event's side effects but must not let a null status wipe a live
      // ON CALL — see the status dispatch at the end of this handler.
      const suppressStatus = verdict.decision === DECISION.PASS;
      callGuardRef.current = { state: verdict.nextState };

      // Learn this call's identifiers from the event stream itself. A call does not have
      // one uuid — the originate response and the switch's own events can disagree — so
      // every accepted event claims its uuid for the call in progress. Without this, only
      // the originate uuid would ever be recognised and each of the switch's own events
      // would look unattributable.
      //
      // The set only ever GROWS during a call, and is emptied in exactly two places: the
      // reconciliation effect when the call ends, and here when a new call arrives before
      // the previous one's wrapup has cleared (the WRAPUP -> ANSWER path).
      //
      // It must NOT reset from IDLE. A manual or progressive call seeds the set with the
      // uuid from the originate response while the guard is still IDLE; if the switch's
      // own first event for that same call carries a different uuid, an IDLE reset would
      // evict the originate uuid — and a teardown later arriving under it would then look
      // like a previous call's, be dropped, and strand the agent ON CALL forever.
      // Accumulating both uuids is the safe direction.
      if (!event.isHangupSignal && event.uuid) {
        const supersedesPreviousCall =
          previousState === GUARD_STATE.WRAPUP && !currentCallUuidsRef.current.has(event.uuid);
        if (supersedesPreviousCall) currentCallUuidsRef.current = new Set();
        markCallUuidCurrent(event.uuid);
      }
      dispatch(onSetCallType(message?.call_type));
      if (message?.call_type === 'HANGUP') {
        dispatch(setCallId(message?.origination_uuid));
        dispatch(setCurrentCallDetail(message));
      }
      const userProcessType = user?.process_details?.type;
      const isIncomingManualCall =
        userProcessType === 'InboundAndManual' ||
        userProcessType === 'BlendedAndPredictive' ||
        userProcessType === 'BlendedAndPreview' ||
        userProcessType === 'BlendedAndProgressive';
      if (
        process.env.REACT_APP_INCOMING_SCREEN === 'true' &&
        isIncomingManualCall &&
        message?.call_type !== 'incoming' &&
        message?.event_type === 'EXTENSION_RINGING'
      ) {
        console.log('Manual Call Answer....');
        answerCall();
      }
      if (
        (message?.call_type === 'predictive' ||
          message?.call_type === 'incoming' ||
          message?.call_type === 'voicebroadcast' ||
          (message?.call_type === 'manual' &&
            window.self !== window.top &&
            message?.event_type !== 'ANSWER')) &&
        (message?.event_type === 'ANSWER' || message?.event_type === 'EXTENSION_RINGING')
      ) {
        // Clear the previous call's CRM details when a genuinely new call arrives,
        // so `!crmDetails` truthfully means "this call has no details yet" and the
        // recovery/watchdog can re-arm. Guarded on the uuid actually changing: this
        // block fires for both EXTENSION_RINGING and ANSWER of the same call, and we
        // must not wipe details already fetched earlier in the same call.
        if (message?.origination_uuid !== callIdRef.current) {
          dispatch(removeCrmDetails());
        }
        dispatch(setCallId(message?.origination_uuid));
        // Keep the ref in lockstep with the dispatch: the syncing effect only runs
        // after commit, and a HANGUP for this same call can arrive before that.
        callIdRef.current = message?.origination_uuid;
        markCallUuidCurrent(message?.origination_uuid);
        dispatch(setCallingModalOpen(true));
        dispatch(setCurrentCallDetail(message));
        getCrmDetails(message);
        dispatch(setCallDialSec(0));
        dispatch(
          setScreenType(
            message?.call_type === 'incoming' &&
              message?.event_type === 'EXTENSION_RINGING' &&
              process.env.REACT_APP_INCOMING_SCREEN === 'true'
              ? 'INCOMING'
              : 'LIVE_CALL'
          )
        );
        if (
          (message?.call_type === 'incoming' &&
            message?.event_type === 'EXTENSION_RINGING' &&
            process.env.REACT_APP_INCOMING_SCREEN === 'true') ||
          (window.self !== window.top && message?.event_type === 'EXTENSION_RINGING')
        ) {
          console.log('message?.destination_number', message?.destination_number);
          setNumber(message?.destination_number);
          dispatch(setCallNumber(message?.destination_number));
        }
      }
      const callType =
        message?.event_type === 'EXTENSION_RINGING'
          ? message?.call_type === 'predictive'
            ? 'ON CALL'
            : 'RINGING'
          : message?.event_type === 'DIALING'
            ? 'DIALING'
            : message?.event_type === 'ANSWER'
              ? 'ON CALL'
              : message?.event_type === 'HANGUP'
                ? 'WRAPUP'
                : null;
      // An event with no status mapping (HOLD, BRIDGE, transfer, anything the backend
      // adds later) used to fall through to `dispatch(onSetCallStatus(null))` below and
      // wipe a live ON CALL. Its side effects above still run; only the status change is
      // suppressed.
      if (suppressStatus) return;
      if (callStatus !== callType) {
        handleTimeReset();
        if (callType === 'WRAPUP') {
          if (callStatus) playBeep();
        } else if (
          // WHEN the beep lands differs by who placed the call:
          //
          //   manual  — the agent dialled it, so RINGING tells them nothing they
          //             do not already know. Beep when the far end ANSWERS, i.e.
          //             on ON CALL. (A manual call runs DIALING -> RINGING ->
          //             ON CALL, so the ring is deliberately skipped here.)
          //
          //   others  — an inbound call is news the moment it rings, so beep on
          //             the first live state. `callStatus !== 'RINGING'` then
          //             suppresses the follow-up RINGING -> ON CALL of that same
          //             call so it only sounds once. Predictive maps
          //             EXTENSION_RINGING straight to ON CALL and beeps there.
          (message?.call_type === 'manual'
            ? callType === 'ON CALL'
            : (callType === 'RINGING' || callType === 'ON CALL') && callStatus !== 'RINGING') &&
          (message?.call_type === 'predictive' ||
            message?.call_type === 'incoming' ||
            message?.call_type === 'voicebroadcast' ||
            message?.call_type === 'manual') &&
          !isRingtonePlaying()
        ) {
          playBeep();
        }
      }
      dispatch(onSetCallStatus(callType));
      if (callType) {
        dispatch(setSelectBreakType(null));
      }
    };

    const handleConnect = () => {
      console.log('Socket connected: ', socket.id);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    const handleReconnect = (attemptNumber) => {
      console.log('Socket reconnected after attempt ', attemptNumber);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    // Timers and the media-ended subscription release a held teardown through this ref
    // so they always run the freshest handler — the effect re-registers on every
    // callStatus change, and a hold can easily outlive one of those cycles.
    handleMessageRef.current = handleMessage;

    socket.on('connect', handleConnect);
    socket.on('reconnect', handleReconnect);
    socket.on('server_restart', handleServerRestart);
    socket.on('sendMessage', handleMessage);

    return () => {
      socket.off('server_restart', handleServerRestart);
      socket.off('sendMessage', handleMessage);
      // Detach only our own handlers. These used to be `socket.off('connect')` with
      // no reference, which wiped every connect/reconnect listener on the shared
      // socket singleton — and since `breakDetails` is in this effect's deps, that
      // happened on every single break transition.
      socket.off('connect', handleConnect);
      socket.off('reconnect', handleReconnect);
    };
  }, [user, callStatus, breakDetails, sipStatus, isMini, window.self !== window.top]);

  const getLogDetails = async (data) => {
    try {
      await dispatch(crmInfoLog(data)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onProgressiveCall = async () => {
    try {
      const response = await dispatch(progressiveCall()).unwrap();
      if (response && response?.data) {
        // A progressive dial is always a fresh call — clear the prior call's details.
        dispatch(removeCrmDetails());
        dispatch(setCallId(response?.data?.origination_uuid));
        callIdRef.current = response?.data?.origination_uuid;
        markCallUuidSeen(response?.data?.origination_uuid);
        dispatch(setCurrentCallDetail(response?.data));
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isMini) return;
    if (
      user &&
      user?.process_details &&
      (user?.process_details?.type === 'OutboundAndProgressive' ||
        user?.process_details?.type === 'BlendedAndProgressive') &&
      !callStatus &&
      !breakType
    ) {
      const apiRefresh = Number(user?.process_details?.ProgressiveDialingSec) || 5;

      const shouldRegister = Number(user.web_phone) === 0 ? sipStatus === 'Register' : true;

      if (shouldRegister) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            onProgressiveCall();
          }, apiRefresh * 1000);
        }
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, callStatus, breakType, sipStatus, isMini]);

  const manualDialingOn = async () => {
    try {
      await dispatch(manualDialing()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (breakType && breakType.name === 'OUTGOING') {
      manualDialingOn();
    }
  }, [breakType]);

  const getBreaksList = async () => {
    try {
      const payload = { process_id: user?.process_details?.process_id };
      await dispatch(breaksListGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBreaksList();
  }, [user]);

  const onUnregisterCall = async () => {
    try {
      await dispatch(setUnregisterCall()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSelectBreak = async (data, type) => {
    const isBreakIn = type === 'in';
    const payload = {
      break_id: data?.id,
      break_name: data?.name,
      type: type
    };

    setIsBreakLoading(true);

    // Latch the break intent synchronously, BEFORE any await below. `onUnregisterCall`
    // already tells the switch to drop this agent, but `breakDetails` — what every
    // guard reads — is only committed two HTTP round-trips later. Anything the switch
    // emits in between (typically the HANGUP caused by this very click) has to be
    // ignored already, or it lands as a WRAPUP status and a CRM screen over the break.
    if (isBreakIn) {
      setBreakPending(true);
      breakDetailsRef.current = payload;
    }

    try {
      if (isBreakIn) {
        await onUnregisterCall();
      }
      window.__SIP_MANUAL_LOGOUT__ = true;
      reconnectInProgressRef.current = true;
      clearRegisterRetry();
      await dispatch(selectBreak(payload)).unwrap();
      dispatch(setBreakDetails(isBreakIn ? payload : null));
      // Keep the ref in lockstep with the dispatch. The syncing effect only runs
      // after the commit, which is too late for a message arriving right now.
      breakDetailsRef.current = isBreakIn ? payload : null;
      if (!isBreakIn) {
        setBreakPending(false);
      }
      dispatch(
        setSelectBreakType(isBreakIn ? { id: data?.id, name: data?.name, color: 'warning' } : null)
      );
      handleTimeReset();
      if (pauseMode === 0) {
        onPauseModeChange(1);
      }
    } catch (error) {
      // The break never took effect. Release the latch, otherwise the agent is
      // silently deaf to call events with no break showing that they can end.
      // A failed break-OUT deliberately keeps the latch: the agent is still on break.
      if (isBreakIn) {
        setBreakPending(false);
        breakDetailsRef.current = breakDetails;
      }
      console.log('error', error);
    } finally {
      setIsBreakLoading(false);
    }
  };

  // Single choke point for every auto-dial writer (follow-up toasts, the 4s redial
  // timers, and `isDialNumber` restored from persistence on reload). An agent on
  // break must never be dialled into a call — drop the request rather than queue it,
  // since by the time the break ends the number is no longer the right one to call.
  useEffect(() => {
    if (isMini) return;
    if (isOnBreakNow()) {
      if (isDialNumber) dispatch(setIsDialNumber(null));
      return;
    }
    if (isDialNumber) {
      dialNumber(isDialNumber);
    }
  }, [isDialNumber, isMini]);

  const dialNumber = async (number) => {
    const numberType = dialNumberType;
    setNumber(number);
    dispatch(setCallNumber(number));
    dispatch(setIsReDial(false));
    dispatch(setIsDialNumber(null));
    dispatch(setDialNumberType(null));
    try {
      const payload = {
        destination_number: number,
        ...(numberType ? { number_type: numberType } : {})
      };
      const response = await dispatch(manualCall(payload)).unwrap();
      if (response && response?.data) {
        // A manual dial is always a fresh call — clear the prior call's details.
        dispatch(removeCrmDetails());
        dispatch(setCallDialSec(0));
        dispatch(setCallId(response?.data?.origination_uuid));
        callIdRef.current = response?.data?.origination_uuid;
        markCallUuidSeen(response?.data?.origination_uuid);
        dispatch(setCurrentCallDetail(response?.data));
        // When embedded in an iframe, show the destination number returned by the
        // manual call response in the live call display.
        if (window.self !== window.top && response?.data?.destination_number) {
          setNumber(response.data.destination_number);
          dispatch(setCallNumber(response.data.destination_number));
        }
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  // Thin wrapper over the shared landing implementation (utils/crmLanding): it
  // serializes concurrent attempts (a socket ANSWER and a watchdog tick must never
  // stack fetches) and latches which call has successfully landed so the watchdog
  // knows when to stop. Break guards, retries, the usable-record check, and the
  // embedded route split all live in the helper.
  const getCrmDetails = async (data) => {
    if (crmFetchInFlightRef.current) return false;
    crmFetchInFlightRef.current = true;
    try {
      const landed = await fetchCrmAndLand({
        dispatch,
        navigate,
        data,
        log: (message, message_data) =>
          getLogDetails({
            user_id: user.user_id,
            message,
            'socket.id': socket.id,
            message_data
          })
      });
      if (landed) {
        landedCallIdRef.current = callIdRef.current;
      }
      return landed;
    } finally {
      crmFetchInFlightRef.current = false;
    }
  };

  // Manually pull the CRM info on demand, then hand the resolved crm_id/unique_id
  // off to the regular getCrmDetails flow so the CRM screen renders as usual.
  const fetchManualCrmInfo = async () => {
    try {
      const response = await dispatch(manualCrmInfo()).unwrap();
      const data = response?.data;
      if (!Array.isArray(data)) return;

      // Keep this socket registered with the server: the session_id stored at
      // login is the socket id known server-side. If it no longer matches the
      // current socket.id (e.g. after a reconnect), re-identify so server-side
      // call events keep routing to this socket.
      const session_id = data.find((item) => item?.user_login_details)?.user_login_details
        ?.session_id;
      if (session_id && session_id !== socket.id) {
        socket.emit('identify', { user_id: user.user_id, type: user?.type });
      }

      const crm_id = data.find((item) => item?.crmDetails)?.crmDetails?.Crms_id;
      const unique_id = data.find((item) => item?.unique_id)?.unique_id;

      if (unique_id) {
        getCrmDetails({ crm_id, unique_id });
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  // A reload with no live call rehydrates the previous call's crmDetails (the whole
  // store persists with no whitelist); clear them once on mount so `!crmDetails`
  // stays a truthful "no details for this call" signal.
  useEffect(() => {
    if (isMini) return;
    if (!callIdRef.current) {
      dispatch(removeCrmDetails());
    }
  }, []);

  // Liveness-bounded CRM-landing watchdog. The one-shot recovery effect this
  // replaces was guarded on `!crmDetails` — permanently disarmed while the previous
  // call's details lingered in state — and its deps never changed on a fetch
  // failure, so a call whose single landing attempt was lost stayed off the CRM for
  // its whole duration (the reported ~1%: agent parked on /dashboard mid-call,
  // recovering only on the next call). This keeps re-trying for as long as the call
  // is genuinely live, and stops the moment the agent has landed once for this call
  // — a deliberate navigation away from /crm afterwards is never fought.
  useEffect(() => {
    if (isMini) return;
    if (!callId || !callStatus || callStatus === 'WRAPUP' || breakDetails) return;
    if (landedCallIdRef.current === callId) return;
    if (!CurrentCallDetail?.unique_id) return;

    const tick = () => {
      if (isOnBreakNow()) return;
      if (landedCallIdRef.current === callIdRef.current) return;
      // Some other path already landed us on the CRM screen — latch and stop.
      const path = window.location.pathname;
      if (path === '/crm' || path === '/call-in-progress') {
        landedCallIdRef.current = callIdRef.current;
        return;
      }
      // getCrmDetails serializes itself, so a tick landing while a previous
      // attempt's retries are still running is simply skipped.
      getCrmDetails(CurrentCallDetail);
    };

    tick();
    const watchdog = setInterval(tick, 5000);
    return () => clearInterval(watchdog);
  }, [callId, callStatus, breakDetails, isMini, CurrentCallDetail]);

  const onIdleSet = async () => {
    // `setIdleCall` marks the agent available server-side, which silently cancels
    // their break as far as the switch is concerned. Callers include timers that can
    // fire mid-break (see DialPadScreen), so the guard has to live here.
    // BreakModal has its own onIdleSet for the deliberate break-out.
    if (isOnBreakNow()) return;
    try {
      await dispatch(setIdleCall()).unwrap();
      setWrapsec(0);
      dispatch(setCallId(null));
      dispatch(setCurrentCallDetail(null));
      dispatch(setCallingModalOpen(user?.manualcall ? true : false));
      dispatch(setCallDialSec(null));
      dispatch(setSidebarType('main'));
      dispatch(setCallEnd(false));
      handleTimeReset();
      dispatch(onSetCallStatus(null));
      dispatch(onSetCallType(null));
      dispatch(setHoldTime(0));
      dispatch(setHoldStartTime(null));
      incomingInvitationRef.current = null;
      if (location.pathname !== '/chat') {
        navigate('/dashboard');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    // Last-resort fallback: force the agent idle if the call is somehow stuck in
    // WRAPUP and the normal auto-submit (wrapsec > autoWrapUp) never ran. This
    // threshold must sit AFTER the configured auto-wrapup window — otherwise a long
    // autoWrapup (e.g. 120s) gets cut short at the old hardcoded 60s, forcing idle
    // before the CRM is ever auto-submitted.
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    const idleFallbackThreshold = Math.max(60, autoWrapUp + 30);
    // Must not fire on break: `setIdleCall` marks the agent IDLE server-side, which
    // silently cancels their break as far as the switch is concerned.
    if (
      callStatus === 'WRAPUP' &&
      wrapsec > idleFallbackThreshold &&
      !isEmbedded &&
      !isMini &&
      !isOnBreakNow()
    ) {
      onIdleSet();
    }
  }, [callStatus, wrapsec, isEmbedded, isMini]);

  const renderScreen = () => {
    switch (screenType) {
      case 'LIVE_CALL':
        return <DefaultLiveCall setNumber={setNumber} number={number} />;

      case 'INCOMING':
        return (
          <IncomingCallScreen
            setNumber={setNumber}
            number={number}
            answerCall={answerCall}
            stopRingtone={stopRingtone}
            incomingInvitationRef={incomingInvitationRef}
          />
        );

      case 'DTMF':
        return <DtmfScreen />;

      case 'TRANSFER':
        return <TransferScreen number={number} />;

      case 'AGENT_TRANSFER':
        return <AgentTransferScreen number={number} />;

      case 'ATTENDED_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'BLIND_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'TRANSFER_HANGUP':
        return <TransferHangupScreen number={number} />;

      case 'CONFERENCE':
        return <ConferenceScreen number={number} />;

      case 'CONFERENCE_LIVE':
        return <ConferenceLiveScreen number={number} setNumber={setNumber} />;

      default:
        return callStatus === 'WRAPUP' ? (
          <div className="input-container">
            <div className="d-flex flex-column gap-2 justify-content-center align-items-center">
              <div
                className="bg-violet rounded-circle d-flex justify-content-center align-items-center cursor-pointer"
                style={{ height: '70px', width: '70px' }}
                onClick={() => {
                  dispatch(setIsReDial(true));
                  socket.emit('identify-redial', { user_id: user.user_id, type: user?.type });
                  dispatch(setCallEnd(true));
                  setNumber('');
                  dispatch(setCallNumber(''));
                  dispatch(setScreenType(null));
                }}>
                <Icon.PhoneForwarded className="text-white" size={30} />
              </div>
              <div className="text-white fs-5 fw-bold">{t('redial')}</div>
            </div>
          </div>
        ) : (
          <DialPadScreen
            number={number}
            setNumber={setNumber}
            setDuration={handleTimeReset}
            duration={duration}
            onIdleSet={onIdleSet}
          />
        );
    }
  };

  const renderCallStatus = () => {
    const callStatusClasses = {
      WRAPUP: 'bg-violet',
      RINGING: 'bg-blue',
      DIALING: 'bg-purple',
      'ON CALL': 'bg-green'
    };

    const statusClass =
      callStatusClasses[callStatus] || (breakType?.color ? `bg-${breakType.color}` : 'bg-info');
    return (
      <div className={`${statusClass} py-1 w-100 text-center text-white rounded-2 idle-time`}>
        <span className="text-uppercase">
          {callStatus
            ? t(callStatus)
            : (breakType?.name && breakType?.name === 'OUTGOING'
                ? t('OUTGOING')
                : breakType?.name) || t('IDLE')}
        </span>{' '}
        <span>
          <Timer duration={duration} setDuration={setDuration} />
        </span>
      </div>
    );
  };

  const renderBreakStatus = () => {
    if (callStatus && callStatus !== 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            onClick={() => {
              getCrmDetails(CurrentCallDetail);
            }}>
            {t('refetchDetails')}
          </Button>
        </div>
      );
    }
    if (callStatus === 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            disabled={location.pathname === '/crm'}
            onClick={() => {
              onIdleSet();
            }}>
            {t('setIdle')}
          </Button>
        </div>
      );
    }
    if ((breakType && breakType?.name === 'OUTGOING') || callStatus !== null) {
      return false;
    }
    return (
      <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
        {breakType ? (
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            disabled={isBreakLoading}
            onClick={() => {
              onSelectBreak(breakType, 'out');
            }}>
            {isBreakLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                />
                {t('ready')}...
              </>
            ) : (
              t('ready')
            )}
          </Button>
        ) : (
          <ButtonDropdown
            className="break-button-dropdown"
            isOpen={isOpen}
            toggle={() => !isBreakLoading && setIsOpen(!isOpen)}>
            <DropdownToggle className="break-button" caret size="sm" disabled={isBreakLoading}>
              {isBreakLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {t('break')}...
                </>
              ) : (
                t('break')
              )}
            </DropdownToggle>
            <DropdownMenu>
              {breaksList.map((val, index) => {
                return (
                  <DropdownItem
                    key={index}
                    onClick={() => {
                      onSelectBreak(val, 'in');
                    }}>
                    {val?.name}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </ButtonDropdown>
        )}
      </div>
    );
  };

  const renderFetchCrmInfo = () => {
    return (
      <div className="px-4 break-container">
        <Button
          caret
          size="sm"
          className="ready-btn w-100 d-flex align-items-center justify-content-center gap-2"
          onClick={() => fetchManualCrmInfo()}>
          <Icon.RefreshCw size={16} />
          {t('fetchCrmInfo')}
        </Button>
      </div>
    );
  };

  const renderProcessDropdown = () => {
    return user?.multi_process_data?.length > 0 ? (
      <div className="px-4">
        <ButtonDropdown
          className="break-button-dropdown"
          isOpen={isConVoxOpen}
          toggle={() => setIsConVoxOpen((prev) => !prev)}>
          <DropdownToggle className="break-button" caret size="sm">
            {user?.process_details?.name}
          </DropdownToggle>

          <DropdownMenu>
            {user.multi_process_data.map((process) => (
              <DropdownItem
                key={process.id}
                active={process.id === user?.process_details?.process_id}
                onClick={() => {
                  if (
                    process.id !== user?.process_details?.process_id &&
                    callStatus === null &&
                    !breakType
                  ) {
                    setIsProcessModalOpen({ isOpen: true, selectedProcess: process });
                  }
                }}>
                {process.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </ButtonDropdown>
      </div>
    ) : (
      <div className="px-4">
        <ButtonDropdown
          className="break-button-dropdown"
          isOpen={isConVoxOpen}
          toggle={() => setIsConVoxOpen(!isConVoxOpen)}>
          <DropdownToggle className="break-button" caret size="sm">
            {user?.process_details?.name}
          </DropdownToggle>
          <DropdownMenu>{user?.process_details?.name}</DropdownMenu>
        </ButtonDropdown>
      </div>
    );
  };

  return (
    <React.Fragment>
      {!user?.manualcall && !callId ? (
        <div className="predictive-container">
          <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
          {renderProcessDropdown()}
          <div className="px-4 py-2">{renderCallStatus()}</div>
          {renderBreakStatus()}
        </div>
      ) : (
        <div className="calling-sidebar p-3 position-relative">
          <div
            className="position-absolute bg-primary shadow-sm rounded-5 top-50 border-white text-white d-flex justify-content-center align-items-center callpase-icon"
            onClick={() => dispatch(setCallingModalOpen(false))}>
            <Icon.ChevronRight />
          </div>
          <div className="shadow-sm dail-page-container rounded-4 py-3">
            <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
            {renderProcessDropdown()}
            <div className="px-4 py-2">{renderCallStatus()}</div>
            {renderScreen()}
            {renderBreakStatus()}
            {!callStatus && renderFetchCrmInfo()}
          </div>
        </div>
      )}
      <MicrophonePermissionModal
        isOpen={showMicModal}
        permissionState={micPermission}
        onEnableClick={requestMicrophonePermission}
        onLogout={onLogout}
      />
    </React.Fragment>
  );
};

CallingSidebar.propTypes = {
  setIsProcessModalOpen: PropTypes.func,
  isMini: PropTypes.bool
};

export default CallingSidebar;
